import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';
import logo from '../../assets/images/Logo-Aone-Final-Full.png';
import moment from 'moment';
import { ToWords } from 'to-words';

const toWords = new ToWords({
    localeCode: 'en-US',
    converterOptions: {
        currency: false,
        ignoreDecimal: false,
        ignoreZeroCurrency: false,
        doNotAddOnly: false
    }
});

const formatCurrency = (num) => {
    if (num === undefined || num === null) return '0';
    return Number(num).toLocaleString('en-US');
};

const styles = StyleSheet.create({
    body: {
        paddingTop: 30,
        paddingBottom: 60,
        paddingHorizontal: 35,
        fontFamily: 'Noto Sans Lao'
    },
    title: {
        padding: 5,
        fontSize: 20,
        textAlign: 'center',
        fontWeight: 'bold'
    },
    section: {
        flexDirection: 'row',
        alignItems: 'center',
        fontSize: 9,
        marginBottom: 2
    },
    label: {
        width: 75,
        fontWeight: 'bold'
    },
    labelRight: {
        width: 75,
        fontWeight: 'bold'
    },
    value: {
        flex: 1
    },
    logo: {
        position: 'absolute',
        top: 20,
        left: 35,
        width: 80
    },
    tableTitle: {
        backgroundColor: '#f8f9fa',
        borderWidth: 1,
        borderColor: '#000',
        padding: 5,
        fontSize: 10,
        fontWeight: 'bold',
        textAlign: 'center',
        marginTop: 10
    },
    table: {
        width: '100%'
    },
    rowTable: {
        flexDirection: 'row',
        fontSize: 9
    },
    headerTable: {
        backgroundColor: '#f8f9fa',
        fontWeight: 'bold',
        borderTopWidth: 1,
        borderTopColor: '#000',
        borderTopStyle: 'solid'
    },
    cell: {
        borderLeftWidth: 1,
        borderLeftColor: '#000',
        borderLeftStyle: 'solid',
        borderBottomWidth: 1,
        borderBottomColor: '#000',
        borderBottomStyle: 'solid',
        padding: 4
    },
    lastCell: {
        borderRightWidth: 1,
        borderRightColor: '#000',
        borderRightStyle: 'solid'
    },
    colNo: { width: '8%' },
    colName: { width: '42%' },
    colQty: { width: '10%' },
    colPrice: { width: '20%' },
    colTotal: { width: '20%' },

    footerContainer: {
        marginTop: 10,
        borderTopWidth: 1,
        borderColor: '#000'
    },
    footerRow: {
        flexDirection: 'row',
        alignItems: 'stretch'
    },
    wordsBox: {
        width: '60%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontSize: 8,
        textAlign: 'center',
        justifyContent: 'center'
    },
    sideLabel: {
        width: '16%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontSize: 9,
        fontWeight: 'bold',
        justifyContent: 'center'
    },
    sideValue: {
        width: '24%',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontSize: 9,
        textAlign: 'right',
        justifyContent: 'center'
    },
    remarkLabel: {
        width: '15%',
        borderLeftWidth: 1,
        borderBottomWidth: 0,
        borderColor: '#000',
        padding: 6,
        fontWeight: 'bold',
        fontSize: 9,
        justifyContent: 'center'
    },
    remarkContent: {
        width: '45%',
        borderLeftWidth: 1,
        borderBottomWidth: 0,
        borderColor: '#000',
        padding: 6,
        fontSize: 9,
        justifyContent: 'center'
    },
    remarkLabelEnd: {
        width: '15%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontWeight: 'bold',
        fontSize: 9,
        justifyContent: 'center'
    },
    remarkContentEnd: {
        width: '45%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 6,
        fontSize: 9,
        justifyContent: 'center'
    },
    grandTotalLabel: {
        width: '60%',
        borderLeftWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 8,
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: 10,
        backgroundColor: '#f8f9fa'
    },
    grandTotalValue: {
        width: '40%',
        borderLeftWidth: 1,
        borderRightWidth: 1,
        borderBottomWidth: 1,
        borderColor: '#000',
        padding: 8,
        textAlign: 'right',
        fontWeight: 'bold',
        fontSize: 10,
        backgroundColor: '#f8f9fa'
    },
    paymentMethodsBox: {
        marginTop: 15,
        borderWidth: 1,
        borderColor: 'blue',
        borderRadius: 10,
        padding: 5
    },
    paymentTitle: {
        color: 'blue',
        fontSize: 10,
        fontWeight: 'bold',
        textAlign: 'center',
        marginBottom: 5
    },
    paymentRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingHorizontal: 10
    },
    paymentCol: {
        width: '48%',
        color: 'blue',
        fontSize: 8,
        fontWeight: 'bold'
    }
});

const MyDocument = ({ data, dataShort }) => {
    const promotionDiscount = data.promotionDiscount || 0;
    const qrPaid = data.qrPaid || 0;
    const creditAmount = data.creditinvoices?.totalCredit || 0;
    // billings.totalPayment is the GROSS order total. Break it into funding sources:
    // the REAL A-one Wallet balance used = gross bill-out − QR − promo − credit.
    const totalKip = data.totalPayment || 0;
    const aoneWallet = Math.max(0, (data.aonewallets?.totalPayment || 0) - qrPaid - promotionDiscount - creditAmount);
    const grandTotal = totalKip - aoneWallet - promotionDiscount - qrPaid - creditAmount;

    return (
        <Document>
            <Page size="A4" style={styles.body}>
                <Image style={styles.logo} src={logo} />
                <Text style={styles.title}>Invoice Summary</Text>

                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginTop: 15, marginBottom: 5 }}>
                    <View style={{ width: '55%' }}>
                        <View style={styles.section}>
                            <Text style={styles.label}>Invoice no.</Text>
                            <Text style={styles.value}>: {data.invoiceNumber}</Text>
                        </View>
                        <View style={styles.section}>
                            <Text style={styles.label}>Customer no.</Text>
                            <Text style={styles.value}>: {data.customerID}</Text>
                        </View>
                        <View style={styles.section}>
                            <Text style={styles.label}>Mobile</Text>
                            <Text style={styles.value}>: {data.customers?.mobile || 'N/A'}</Text>
                        </View>
                        <View style={styles.section}>
                            <Text style={styles.label}>Customer</Text>
                            <Text style={styles.value}>: {data.customers?.customerName || 'N/A'}</Text>
                        </View>
                    </View>
                    <View style={{ width: '40%' }}>
                        <View style={styles.section}>
                            <Text style={styles.labelRight}>Date</Text>
                            <Text style={styles.value}>: {moment(data.createdAt).format('DD-MM-yyyy HH:mm')}</Text>
                        </View>
                        <View style={styles.section}>
                            <Text style={styles.labelRight}>BranchCode</Text>
                            <Text style={styles.value}>: {data.branchCode}</Text>
                        </View>
                        <View style={styles.section}>
                            <Text style={styles.labelRight}>PaymentType</Text>
                            <Text style={styles.value}>: {data.paymentType === 1 ? 'Cash' : data.paymentType === 2 ? 'Bank' : 'Credit'}</Text>
                        </View>
                    </View>
                </View>
                {data?.reference && (
                    <View style={[styles.section, { marginBottom: 10 }]}>
                        <Text style={{ fontWeight: 'bold', width: 90 }}>FinanceComment:</Text>
                        <Text style={{ flex: 1 }}>{data.reference}</Text>
                    </View>
                )}

                {/* Traditional Products */}
                {dataShort?.length > 0 && (
                    <>
                        <Text style={styles.tableTitle}>Traditional Product</Text>
                        <View style={styles.table}>
                            <View style={[styles.rowTable, styles.headerTable]} fixed>
                                <Text style={[styles.cell, styles.colNo]}>No.</Text>
                                <Text style={[styles.cell, styles.colName]}>Product Name</Text>
                                <Text style={[styles.cell, styles.colQty]}>Qty</Text>
                                <Text style={[styles.cell, styles.colPrice]}>Price(₭)</Text>
                                <Text style={[styles.cell, styles.colTotal, styles.lastCell]}>Total(₭)</Text>
                            </View>
                            {dataShort.map((row, i) => (
                                <View key={i} style={styles.rowTable} wrap={false}>
                                    <Text style={[styles.cell, styles.colNo]}>{i + 1}</Text>
                                    <Text style={[styles.cell, styles.colName]}>{row.productName}</Text>
                                    <Text style={[styles.cell, styles.colQty]}>{row.amount}</Text>
                                    <Text style={[styles.cell, styles.colPrice]}>{formatCurrency(row.price)}</Text>
                                    <Text style={[styles.cell, styles.colTotal, styles.lastCell]}>{formatCurrency(row.price * row.amount)}</Text>
                                </View>
                            ))}
                            {/* Total quantity row */}
                            <View style={[styles.rowTable, { backgroundColor: '#f8f9fa' }]} wrap={false}>
                                <Text style={[styles.cell, styles.colNo]}> </Text>
                                <Text style={[styles.cell, styles.colName, { textAlign: 'right', fontWeight: 'bold' }]}>Total Qty</Text>
                                <Text style={[styles.cell, styles.colQty, { fontWeight: 'bold' }]}>
                                    {dataShort.reduce((s, r) => s + Number(r.amount || 0), 0)}
                                </Text>
                                <Text style={[styles.cell, styles.colPrice]}> </Text>
                                <Text style={[styles.cell, styles.colTotal, styles.lastCell]}> </Text>
                            </View>
                        </View>
                    </>
                )}

                {/* Premiums Products */}
                {data?.premiumstocks?.length > 0 && (
                    <>
                        <Text style={styles.tableTitle}>Premiums Product</Text>
                        <View style={styles.table}>
                            <View style={[styles.rowTable, styles.headerTable]} fixed>
                                <Text style={[styles.cell, styles.colNo]}>No.</Text>
                                <Text style={[styles.cell, { width: '22%' }]}>Premium ID</Text>
                                <Text style={[styles.cell, { width: '30%' }]}>Premium Name</Text>
                                <Text style={[styles.cell, { width: '20%' }]}>Qty</Text>
                                <Text style={[styles.cell, { width: '20%' }, styles.lastCell]}>Total(₭)</Text>
                            </View>
                            {data.premiumstocks.map((row, i) => (
                                <View key={i} style={styles.rowTable} wrap={false}>
                                    <Text style={[styles.cell, styles.colNo]}>{i + 1}</Text>
                                    <Text style={[styles.cell, { width: '22%' }]}>{row.premium_id}</Text>
                                    <Text style={[styles.cell, { width: '30%' }]}>{row.premiumName}</Text>
                                    <Text style={[styles.cell, { width: '20%' }]}>{row.amount}</Text>
                                    <Text style={[styles.cell, { width: '20%' }, styles.lastCell]}>{formatCurrency(row.value * row.amount)}</Text>
                                </View>
                            ))}
                        </View>
                    </>
                )}

                {/* Combined Footer Section - FLAT STRUCTURE */}
                <View style={styles.footerContainer} wrap={false}>
                    {/* Row 1: Total */}
                    <View style={styles.footerRow}>
                        <View style={styles.wordsBox}><Text>{toWords.convert(totalKip)}</Text></View>
                        <View style={styles.sideLabel}><Text>Total ₭</Text></View>
                        <View style={styles.sideValue}><Text>{formatCurrency(totalKip)}</Text></View>
                    </View>

                    {/* Row 2: Remark Top + A-one Wallet */}
                    <View style={styles.footerRow}>
                        <View style={styles.remarkLabel}><Text>Remark</Text></View>
                        <View style={styles.remarkContent}><Text>{data.description || '-'}</Text></View>
                        <View style={styles.sideLabel}><Text>A-one Wallet</Text></View>
                        <View style={styles.sideValue}><Text>{`-${formatCurrency(aoneWallet)}`}</Text></View>
                    </View>

                    {qrPaid > 0 && (
                        <View style={styles.footerRow}>
                            <View style={styles.remarkLabelEnd}><Text></Text></View>
                            <View style={styles.remarkContentEnd}><Text></Text></View>
                            <View style={styles.sideLabel}><Text>QR Payment</Text></View>
                            <View style={styles.sideValue}><Text>{`-${formatCurrency(qrPaid)}`}</Text></View>
                        </View>
                    )}

                    {/* Promotion Discount — left cell shows the cashback source label */}
                    <View style={styles.footerRow}>
                        <View style={styles.remarkLabelEnd}><Text></Text></View>
                        <View style={styles.remarkContentEnd}><Text style={{ fontSize: 8 }}>{data.promotionLabel || ''}</Text></View>
                        <View style={styles.sideLabel}><Text>Promotion Discount</Text></View>
                        <View style={styles.sideValue}><Text>{`-${formatCurrency(promotionDiscount)}`}</Text></View>
                    </View>

                    {creditAmount > 0 && (
                        <View style={styles.footerRow}>
                            <View style={styles.remarkLabelEnd}><Text></Text></View>
                            <View style={styles.remarkContentEnd}><Text style={{ fontSize: 8 }}>{data.creditinvoices?.invoiceNumber || ''}</Text></View>
                            <View style={styles.sideLabel}><Text>Credit Invoice</Text></View>
                            <View style={styles.sideValue}><Text>{`-${formatCurrency(creditAmount)}`}</Text></View>
                        </View>
                    )}

                    {/* Row 4: Grand Total */}
                    <View style={styles.footerRow}>
                        <View style={styles.grandTotalLabel}><Text>Grand Total</Text></View>
                        <View style={styles.grandTotalValue}><Text>{formatCurrency(grandTotal)}</Text></View>
                    </View>
                </View>

                {/* ── Cashback promotion summary (v2 announcements) ── */}
                {Array.isArray(data.eligibleAnnouncements) && data.eligibleAnnouncements.length > 0 && (
                    <View
                        wrap={false}
                        style={{
                            marginTop: 8,
                            marginBottom: 8,
                            borderWidth: 1.5,
                            borderColor: '#2E7D32',
                            borderStyle: 'solid',
                            borderRadius: 6,
                            padding: 8,
                            backgroundColor: '#F5FBF5'
                        }}
                    >
                        <Text style={{ fontSize: 11, fontWeight: 'bold', color: '#2E7D32', marginBottom: 4 }}>
                            ໂປຣໂມຊັ່ນທີ່ລູກຄ້າຈະໄດ້ຮັບ (Cashback / ຂອງແຖມ)
                        </Text>
                        {data.eligibleAnnouncements.map((a, ai) => (
                            <View
                                key={ai}
                                style={{
                                    marginBottom: 4,
                                    paddingTop: 3,
                                    paddingBottom: 3,
                                    borderTopWidth: ai === 0 ? 0 : 0.5,
                                    borderTopColor: '#c8e6c9',
                                    borderTopStyle: 'solid'
                                }}
                            >
                                <Text style={{ fontSize: 10, fontWeight: 'bold' }}>
                                    {a.announcementNo}{a.header ? `  -  ${a.header}` : ''}
                                </Text>
                                {(a.packs || []).map((p, pi) => {
                                    const premiumsText = (p.premiums || [])
                                        .map((pr) => `${pr.premiumName || pr.premium_id} x${pr.qty}`)
                                        .join(', ');
                                    return (
                                        <Text key={pi} style={{ fontSize: 9, marginTop: 1 }}>
                                            - [{p.matchType}] {p.packId}{p.packName ? ` (${p.packName})` : ''} x {p.applicablePacks} : Cashback {formatCurrency(p.cashback || 0)}
                                            {premiumsText ? `   Gift: ${premiumsText}` : ''}
                                        </Text>
                                    );
                                })}
                                <Text style={{ fontSize: 10, color: '#2E7D32', fontWeight: 'bold', marginTop: 2 }}>
                                    ລວມ Cashback: {formatCurrency(a.totalCashback || 0)}
                                </Text>
                            </View>
                        ))}
                    </View>
                )}

                {/* Signatures */}
                <View style={{ marginTop: 20, flexDirection: 'row', justifyContent: 'space-between' }} wrap={false}>
                    <View style={{ width: '48%', textAlign: 'left' }}>
                        <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Approved By: {data.ApproveBy}</Text>
                        <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                        <Text style={{ fontSize: 9, marginTop: 5 }}>Date : ............................................................</Text>
                    </View>
                    <View style={{ width: '48%', textAlign: 'left' }}>
                        <Text style={{ fontSize: 10, fontWeight: 'bold' }}>Customer: {data.customers?.customerName}</Text>
                        <Text style={{ fontSize: 9, marginTop: 10 }}>Signature : .....................................................</Text>
                        <Text style={{ fontSize: 9, marginTop: 5 }}>Date : ............................................................</Text>
                    </View>
                </View>

                {/* Methods of Payment */}
                <View style={styles.paymentMethodsBox} wrap={false}>
                    <Text style={styles.paymentTitle}>METHODS OF PAYMENT</Text>
                    <View style={styles.paymentRow}>
                        <View style={styles.paymentCol}>
                            <Text>Bank Name: BCEL BANK CO.,LTD</Text>
                            <Text>Account Name : A ONE DISTRIBUTORS CO.,LTD</Text>
                            <Text>Accout No. 010/110/00/60034 USD</Text>
                            <Text>Accout No. 010/110/00/60025 THB</Text>
                            <Text>Accout No. 010/110/00/60016 LAK</Text>
                        </View>
                        <View style={[styles.paymentCol, { textAlign: 'right' }]}>
                            <Text>Bank Name: BIC BANK</Text>
                            <Text>Account Name : A ONE DISTRIBUTORS CO.,LTD</Text>
                            <Text>Accout No. 0000-1010-0195-201 USD</Text>
                            <Text>Accout No. 0000-1010-0195-109 THB</Text>
                            <Text>0000-1010-0195-006 LAK</Text>
                        </View>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

const InvoicePDF_Short = ({ data, dataShort }) => {
    if (!data) return null;
    return <MyDocument data={data} dataShort={dataShort} />;
};

export default InvoicePDF_Short;
